import React, { useEffect, useMemo, useState, createRef, useRef } from 'react';
import {
  EditBioField,
  EditIssueMemoized,
  PhotoSelectFromMulti,
  FieldInlineEdit,
  DataField,
  PhotoUploadInput,
  TabBarContinuous
} from '../index';
import styled from 'styled-components'
import { Button, TextField, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useFeathers } from '../../app/util';
import { coveragePlanSpecifications } from '../../app/config';
import LinkList from '../LinkList/LinkList';
import { useAreaTimeTracker } from '../../app/hooks/useAreaTimeTracker';
import { mapDraftToServerInputs } from './map-draft-to-server-inputs';
import { resetCandidateProfile } from './reset-candidate-profile';

/* A general purpose component for editing a candidate's profile, to work with the new stances model in issues.
  *  allows editing of photos, bios, and issues.
  * Links (references) handled separately.
*/

// Call this function to update the draft object after an error is returned from the server
const newDraftAfterError = (draft, error) => {
  if(error?.data?.issueKey && typeof error?.data?.stanceIndex === 'number') {
    const {
      issueKey,
      stanceIndex,
      field
    } = error.data;
    const issue = draft.issues.find(iss => iss.key === issueKey);
    const stanceId = issue ? issue.stances?.[stanceIndex]?._id : null;

    return {
      ...draft,
      issues: draft.issues.map(iss => {
        if(iss.key === issueKey) {
          return {
            ...iss,
            stances: iss.stances.map((s) => {
              if(s._id === stanceId) {
                return {
                  ...s,
                  hasError: field
                };
              }
              return s;
            })
          };
        }
        return iss;
      })
    };
  }

  return draft;
}

const EditCandidateProfile = (
  {
    race,
    candidateId,
    electionKey,
    photoOptions,
    disabled,

    // specifically corresponds to the candidate staging draft object
    value,
    onChange,
    onChangeIssue,
    coveragePlan = 'auto-coverage',
    reset = 0,
    onTrackedTimesUpdate // callback to send tracked times to parent
  }
) => {
  const feathers = useFeathers();
  const [ activeStanceId, setActiveStanceId ] = useState(null);
  const [ activeBioField, setActiveBioField ] = useState(null);
  const [ detailedIssueData, setDetailedIssueData ] = useState({});
  const [ editingName, setEditingName ] = useState(false);
  const [ isPageVisible, setIsPageVisible ] = useState(!document.hidden);
  const [ activeSectionIndex, setActiveSectionIndex ] = useState(0);
  const [ summarizeExamples, setSummarizeExamples ] = useState([]);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;
  const fieldsForCoveragePlan = coveragePlanSpecifications[coveragePlan].progressArrayForCandidate({});
  const biosEnabled = (fieldsForCoveragePlan || []).some(spec => spec.field.toLowerCase().includes('background'))

  // Refs for each scrollable section (bios + issues).
  // Lazily initialize per index so we don't have to resync the whole array on every render.
  const sectionsRef = useRef([]);

  // Track page visibility for time tracking
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!reset) return;
    onChangeRef.current(resetCandidateProfile(valueRef.current));
  }, [reset]);

  // Calculate the active area based on which section is visible in the viewport
  const activeArea = useMemo(() => {
    const numBioSections = biosEnabled ? 3 : 0;
    
    if (activeSectionIndex < numBioSections) {
      return 'bios';
    } else {
      return 'issues';
    }
  }, [activeSectionIndex, biosEnabled]);

  // Store the latest callback in a ref to avoid dependency issues
  const onTrackedTimesUpdateRef = useRef(onTrackedTimesUpdate);
  useEffect(() => {
    onTrackedTimesUpdateRef.current = onTrackedTimesUpdate;
  }, [onTrackedTimesUpdate]);

  // Use the area time tracker
  const { trackedTimes, getAndResetTrackedTimes } = useAreaTimeTracker(
    isPageVisible,
    activeArea
  );

  // Expose tracked times to parent component
  useEffect(() => {
    if (onTrackedTimesUpdateRef.current && Object.keys(trackedTimes).length > 0) {
      onTrackedTimesUpdateRef.current(trackedTimes);
    }
  }, [trackedTimes]);

  const hasWebsite = useMemo(() => {
    return value?.references?.categories?.find(r => r.type === 'website')?.sources?.length > 0;
  }, [ value?.references ])

  const issuesCovered = useMemo(() => {
    return (value?.issues || []).map((iss) => iss.key)
  }, [ value?.issues ])

  const loadIssuesCovered = async () => {
    // On initial load, always include the ethics & conduct issue in the details lookup,
    // even if it is not yet in the candidate's issues list (so the "Add Ethics and Conduct"
    // button has the metadata it needs). We still only render issues that exist on the draft.
    const keysForDetails = [...new Set([...(issuesCovered || []), 'ethics'])];

    // do a get request against the issues service to get detailed data
    const loading = keysForDetails.reduce((acc, iss) => {
      acc[iss] = { loading: true }
      return acc;
    }, {})
    setDetailedIssueData(d => ({
      ...loading,
      ...d,
    }))

    try {
      const details = await feathers.getService('issues').find({
        query: {
          key: keysForDetails,
          $limit: keysForDetails.length
        }
      })

      const byKey = details.data.reduce((acc, iss) => {
        acc[iss.key] = iss;
        return acc;
      }, {})
      setDetailedIssueData(d => ({
        ...d,
        ...byKey
      }))
    } catch(err) {
      console.log(err)
      const res = keysForDetails.reduce((acc, iss) => {
        acc[iss] = { error: true }
        return acc;
      }, {})

      setDetailedIssueData(d => ({
        ...d,
        ...res
      }))
    }
  }

  useEffect(() => {
    if(feathers && issuesCovered?.length > 0) loadIssuesCovered();
  }, [ feathers, issuesCovered ])

  const summarizeExampleIds = useMemo(() => {
    return [...new Set(
      (value?.issues || []).flatMap((issue) => 
        issue.stances.map((stance) => stance.autoSummary?.summarizeExamples).filter(Boolean)
      ).filter(Boolean)
      .flat()
    )];
  }, [ value?.issues ])

  // Load all summarize examples on first render
  useEffect(() => {
    if (!feathers || (summarizeExampleIds || []).length === 0) return;
    
    const loadSummarizeExamples = async () => {
      try {
        const total = summarizeExampleIds.length;
        const pageLimit = 19;
        const pages = Math.ceil(total / pageLimit);

        // Load all pages in parallel, chunking IDs into blocks of 50
        const pagePromises = Array.from({ length: pages }, async (_, i) => {
          const ids = summarizeExampleIds.slice(i * pageLimit, (i + 1) * pageLimit);
          console.log(ids);
          const response = await feathers.getService('summarize-examples').find({
            query: {
              _id: ids,
              $limit: pageLimit
            }
          });
          return response.data;
        });

        const allPages = await Promise.all(pagePromises);
        const examples = allPages.flat();

        // Create a map by _id for quick lookup
        const examplesMap = examples.reduce((acc, ex) => {
          acc[ex._id] = ex;
          return acc;
        }, {});
        setSummarizeExamples(examplesMap);
      } catch (err) {
        console.error('Error loading summarize examples:', err);
      }
    };

    loadSummarizeExamples();
  }, [feathers, summarizeExampleIds]);

  const possibleSources = useMemo(() => {
    // a list of possible sources available to the editor
    return (value?.references?.categories || []).map(cat => {
      return (cat.sources || []).map(source => {
        let displayAs;
        if(cat.type === 'website') displayAs = 'Candidate website';
        else if(cat.type === 'questionnaire') displayAs = source.title || 'Branch questionnaire';
        else if(source.title) displayAs = source.title;

        return {
          displayAs,
          value: source
        }
      })
    }).flat()
  }, [ value?.references ])

  const issueOptions = useMemo(() => {
    if(!value?.issues) return [];
    return value.issues.map((issue) => ({
      key: issue.key,
      title: issue.title,
      subtopics: detailedIssueData[issue.key]?.subtopics
    }))
  }, [ detailedIssueData ]);

  const linksFromReferences = useMemo(() => {
    return (value?.references?.categories || []).map(category => {
      if(category?.type === 'website') {
        return {
          mediaType: 'website',
          url: category?.sources?.[0]?.url
        }
      } else if(category.type === 'social') {
        return category.sources.map(source => ({
          mediaType: source?.mediaType,
          url: source?.url
        }))
      } else {
        return null
      }
    }).flat().filter(Boolean)
  }, [ value?.references ])

  const handleBioFieldFocus = (e, bioField) => {
    setActiveBioField(bioField);
    setActiveStanceId(null); // Deactivate stance when bio field is focused
  };

  return (
    <Wrapper>
      {
        value && biosEnabled &&
        <TabBarContinuous
          sections={[
            ...(biosEnabled ? [
              {
                title: 'Political Background',
                ref: (sectionsRef.current[0] || (sectionsRef.current[0] = createRef()))
              },
              {
                title: 'Professional Background',
                ref: (sectionsRef.current[1] || (sectionsRef.current[1] = createRef()))
              },
              {
                title: 'Personal Background',
                ref: (sectionsRef.current[2] || (sectionsRef.current[2] = createRef()))
              },
            ] : []),
            ...((value?.issues || []).map((issue, issueIdx) => ({
              title: issue.title,
              ref: (
                sectionsRef.current[issueIdx + (biosEnabled ? 3 : 0)] ||
                (sectionsRef.current[issueIdx + (biosEnabled ? 3 : 0)] = createRef())
              )
            })))
          ]}
          style={{ marginBottom: '24px' }}
          onActiveTabChange={setActiveSectionIndex}
        />
      }
      <WrapperInner>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px'}}>
          <PhotoUploadInput
            style={{
              margin: '0 0 16px',
              alignSelf: 'center',
              maxWidth: '700px'
            }}
            value={value?.photoPathFace}
            onChange={(v) => onChange({ ...value, photoPathFace: v })}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {
              editingName
              ? <FieldInlineEdit
                  inputComponent={
                    <TextField
                      value={value?.name}
                      name={'name'}
                      onChange={(e) => onChange({
                        ...value,
                        name: e.target.value
                      })}
                      style={{ width: 'calc(100% - 15px)' }}
                    />
                  }
                  onCancel={() => setEditingName(false)}
                  onSave={() => setEditingName(false)}
                />
              :
                <DataField onEdit={() => setEditingName(true)}>
                  <Typography variant='h3'>{value?.name}</Typography>
                </DataField>
            }
            <Typography variant='body1'>
              Running for {race?.officeName} in {race?.district?.longName} in {race?.election?.stateName}.
            </Typography>
            <Link to={`/elections/${electionKey}/races/${race?._id}/candidates/${candidateId}`} target='_blank'>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}>
                <Typography variant='body2'>Full page</Typography>
                <OpenInNewIcon style={{ width: '15px', height: '15px' }}/>
              </div>
            </Link>
          </div>
        </div>
        { (photoOptions || []).length > 0 && // todo: enable this
          <div style={{ margin: '0 24px 24px' }}>
            <PhotoSelectFromMulti
              size={value?.photoPathFace ? 'small' : 'medium'}
              photoOptions={photoOptions}
              onSelect={(e) => onChange({
                  ...value,
                  photoPathFace: e
                })
              }
            />
          </div>
        }
        {
          (linksFromReferences?.length > 0) &&
          <div style={{ marginTop: '-20px', paddingLeft: '100px' }}>
            <LinkList
              links={linksFromReferences}
              align='flex-start'
            />
          </div>
        }
        {
          biosEnabled &&
          ['personal', 'professional', 'political'].map((bioFieldKey, bioIndex) => {
            const bioField = value?.bios?.find(b => b.type === bioFieldKey) || {
              type: bioFieldKey,
              text: '',
              sources: [],
              complete: false
            };
            return (
              <EditBioField
                ref={(
                  sectionsRef.current[bioIndex] ||
                  (sectionsRef.current[bioIndex] = createRef())
                )}
                hasWebsite={reset ? false : hasWebsite}
                key={bioFieldKey}
                title={namedField[bioFieldKey]}
                value={bioField}
                possibleSources={possibleSources}
                candidateName={value?.name}
                raceName={race?.name}
                office={race?.office?.name}
                bioType={bioFieldKey}
                onChange={(e) => {
                  if(!value?.bios?.find(b => b.type === bioFieldKey)) {
                    onChange({
                      ...value,
                      bios: [...value?.bios, bioField]
                    })
                  } else {
                    const newBios = value?.bios?.map(b => {
                      if(b.type === bioFieldKey) {
                        return {
                          ...b,
                          ...e
                        }
                      }
                      return b;
                    })
                    onChange({
                      ...value,
                      bios: newBios
                    })
                  }
                }}
                disabled={disabled}
                active={activeBioField === bioFieldKey}
                onFocus={(e) => handleBioFieldFocus(e, bioFieldKey)}
              />
            )
          })
        }
        <HorizontalSeperator />
        {(value?.issues || []).map((issue, issueIdx, arr) => (
          <React.Fragment key={issue?.key}>
            <EditIssueMemoized
              ref={(
                sectionsRef.current[issueIdx + (biosEnabled ? 3 : 0)] ||
                (sectionsRef.current[issueIdx + (biosEnabled ? 3 : 0)] = createRef())
              )}
              candidateName={value?.name}
              office={race?.office?.name}
              hasWebsite={reset ? false : hasWebsite}
              detailedIssue={detailedIssueData[issue.key]}
              key={issue?.key}
              issueName={issue.title}
              value={issue}
              enableScrollToActive={true}
              possibleSources={possibleSources}
              disabled={disabled}
              issueOptions={issueOptions}
              activeStanceId={activeStanceId}
              summarizeExamples={summarizeExamples}
              onFocusStance={(e, newStanceIndex) => {
                setActiveStanceId(newStanceIndex)
              }}
              onReassignStance={(newIssue, stance) => {
                // Take the stance and assign it to the new category

                // First, we modify the stance issuesSecondary field to remove the current issue or new issue
                const stanceNew = {
                  ...stance,
                  issuesSecondary: (stance.issuesSecondary || []).filter((issueKey) => ((issueKey === newIssue) || (issueKey === stance.issue)) ? false : true)
                }

                const issueNewIndex = value.issues.findIndex((i) => i.key === newIssue);
                if(issueNewIndex < 0) throw new Error(`No issue with key ${newIssue} found`);
                const newIssues = value.issues.map((issue, i) => {
                  if(i === issueIdx) {
                    // return this issue without the stance at stanceIdx
                    return {
                      ...issue,
                      stances: issue.stances.filter((s) => s._id !== stanceNew._id)
                    }
                  } else if(i === issueNewIndex) {
                    return {
                      ...issue,
                      stances: [].concat(issue.stances, stanceNew)
                    }
                  } else {
                    return issue;
                  }
                })
                setActiveStanceId(stanceNew._id)
                onChange({
                  ...value,
                  issues: newIssues
                })
              }}
              onChange={(newIssue) => onChangeIssue(newIssue, issue.key)}
            />
            {issueIdx !== arr.length && <HorizontalSeperator />}
          </React.Fragment>
        ))}
        {
          // Allow researchers to add the Ethics & Conduct issue if it isn't already present
          (value?.issues || []).every((issue) => issue.key !== 'ethics') && (
            <div>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  const newIssue = {
                    key: 'ethics',
                    title: detailedIssueData['ethics']?.name || 'Ethics and Conduct',
                    stances: [],
                    sources: [],
                    complete: false,
                  };

                  onChange({
                    ...value,
                    issues: [...(value?.issues || []), newIssue],
                    issuesToCoverAdditional: [
                      ...(value?.issuesToCoverAdditional || []),
                      'ethics',
                    ],
                  });
                }}
                disabled={disabled}
              >
                Add Ethics and Conduct
              </Button>
            </div>
          )
        }
      </WrapperInner>
    </Wrapper>
  )
}


const HorizontalSeperator = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e0e0e0;
`


const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 36px;
`

const WrapperInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;
  max-width: 800px;
`

const namedField = {
  'personal': 'Personal background',
  'professional': 'Professional background',
  'political': 'Political background'
}

export { EditCandidateProfile, mapDraftToServerInputs, newDraftAfterError };